/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useRef } from 'react';
import type { EntryPoint, EntryPointProps, PreloadedEntryPoint, PreloadedQuery } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { SideNavBody } from '@atlaskit/navigation-system/layout/side-nav';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { sidebarNav4Resource } from '@jira/navigation-apps__resources/src/services/sidebar-nav4/index.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { SidebarReady } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/metrics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { JiraHoldoutCustomData } from '@atlassian/holdout-utils/jira-holdout';
import type { L1MenuItemsQuery } from './ui/__generated__/L1MenuItemsQuery.graphql';
import { PACKAGE_NAME, TEAM_NAME } from './common/constants/index.tsx';
import { L1MenuItems } from './ui/L1MenuItems.tsx';
import type { GlobalShortcutsInvitedAppShortcutsRecommendationsComponent } from './ui/configurable-menu-items/global-shortcuts/invited-app-shortcuts-recommendations/GlobalShortcutsInvitedAppShortcutsRecommendations.tsx';

export const SidebarContent = (): React.JSX.Element => {
	const isAnonymous = useIsAnonymous();
	const { queryReference } = useRelayResource<L1MenuItemsQuery>(sidebarNav4Resource);
	const queryReferenceRef = useRef<PreloadedQuery<L1MenuItemsQuery> | null>(null);

	if (isAnonymous) {
		return <SidebarContentInnerAnonymous />;
	}

	// We don't want to use null query references. So only remember the non-null ones,
	// and pass the non-null query reference to the children.
	// From Slack: https://atlassian.slack.com/archives/C01E2MPPLE4/p1654736794416059?thread_ts=1654051102.548519&cid=C01E2MPPLE4
	if (queryReference) {
		queryReferenceRef.current = queryReference;
	}

	return <SidebarContentInner queryReference={queryReferenceRef.current} />;
};

// Named alias avoids TS widening `entryPoints` to `never` when destructured
// at the parameter position. `TNestedEntryPoints` must use `EntryPoint<…>`
// (the unloaded definition) — the framework lifts each to a
// `PreloadedEntryPoint<…>` on the runtime prop. Same as PUMA-2000.
type EntryPointParams = EntryPointProps<
	{ nav4SidebarContentCore: L1MenuItemsQuery },
	{
		invitedAppShortcutsRecommendations?: EntryPoint<
			GlobalShortcutsInvitedAppShortcutsRecommendationsComponent,
			{ cloudId: string }
		>;
	},
	{},
	{}
>;

export function SidebarContentForEntryPoint(props: EntryPointParams): React.JSX.Element {
	const {
		queries: { nav4SidebarContentCore },
		entryPoints,
	} = props;
	const isAnonymous = useIsAnonymous();

	if (isAnonymous) {
		return <SidebarContentInnerAnonymous />;
	}

	return (
		<SidebarContentInner
			queryReference={nav4SidebarContentCore}
			invitedAppShortcutsRecommendationsEntryPoint={entryPoints?.invitedAppShortcutsRecommendations}
		/>
	);
}

type Props = {
	invitedAppShortcutsRecommendationsEntryPoint?: PreloadedEntryPoint<GlobalShortcutsInvitedAppShortcutsRecommendationsComponent>;
	queryReference: PreloadedQuery<L1MenuItemsQuery> | null;
};

// For Storybook, but it also fixes https://hello.atlassian.net/wiki/spaces/~70121adfd59d619854e929bc192ed5ecb9b51/pages/3616160535
// By passing the queryReference as a param, it makes it easier to use for StoryBook
export function SidebarContentInner({
	queryReference,
	invitedAppShortcutsRecommendationsEntryPoint,
}: Props): React.JSX.Element {
	return (
		<UFOSegment name="nav4-sidebar-content">
			<JiraHoldoutCustomData />
			<JSErrorBoundary id="l1-nav-menu-items" packageName={PACKAGE_NAME} teamName={TEAM_NAME}>
				<SideNavBody>
					<MenuList>
						<Placeholder name="l1-nav-menu-items" fallback={null}>
							{queryReference && (
								<L1MenuItems
									queryReference={queryReference}
									invitedAppShortcutsRecommendationsEntryPoint={
										invitedAppShortcutsRecommendationsEntryPoint
									}
								/>
							)}
						</Placeholder>
					</MenuList>
				</SideNavBody>
				<SidebarReady />
			</JSErrorBoundary>
		</UFOSegment>
	);
}

export function SidebarContentInnerAnonymous(): React.JSX.Element {
	const { formattedMessage: projectsMessage, icon: projectsIcon } =
		useConfigurableMessageAndIconMap(L1_MENU_ID.PROJECTS);
	const { formattedMessage: dashboardsMessage, icon: dashboardsIcon } =
		useConfigurableMessageAndIconMap(L1_MENU_ID.DASHBOARDS);
	const { formattedMessage: filtersMessage, icon: filtersIcon } = useConfigurableMessageAndIconMap(
		L1_MENU_ID.FILTERS,
	);

	// We want to remove useRouter but the anonymous path is not as urgent as the authenticated path
	const [{ match }] = useRouter();

	return (
		<JSErrorBoundary id="l1-nav-menu-items" packageName={PACKAGE_NAME} teamName={TEAM_NAME}>
			<SideNavBody>
				<MenuList>
					<Placeholder name="l1-nav-menu-items" fallback={null}>
						<LinkMenuItem
							elemBefore={projectsIcon}
							href="/jira/projects"
							isSelected={match.path.startsWith('/jira/projects')}
						>
							{projectsMessage}
						</LinkMenuItem>
						<LinkMenuItem
							elemBefore={filtersIcon}
							href="/jira/filters"
							isSelected={match.path === '/jira/filters' || match.path.startsWith('/issues/')}
						>
							{filtersMessage}
						</LinkMenuItem>
						<LinkMenuItem
							elemBefore={dashboardsIcon}
							href="/jira/dashboards"
							isSelected={match.path.startsWith('/jira/dashboards')}
						>
							{dashboardsMessage}
						</LinkMenuItem>
					</Placeholder>
				</MenuList>
			</SideNavBody>
			<SidebarReady />
		</JSErrorBoundary>
	);
}

export default SidebarContentForEntryPoint;
